import { useIsVisible } from '@folklore/hooks';
import classNames from 'classnames';
import { useEffect, useId, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'wouter';

import { useAccess, useHasAccess } from '../../hooks/useAccess';
import useCollection from '../../hooks/useCollection';
import { useDocumentProgress } from '../../hooks/useDocument';
import useDocuments from '../../hooks/useDocuments';
import { useDocumentSurtitle } from '../../hooks/useSurtitle';
import { useDocumentUrlGenerator } from '../../hooks/useUrlGenerator';
import { samePageHistoryState } from '../../lib/timeline';

import { audioPlaybackRates, getAudioUrl, useAudioPlayer } from '../../contexts/AudioPlayerContext';
import BasicButton from '../buttons/BasicButton';
import MuteButton from '../buttons/MuteButton';
import PlayButton from '../buttons/PlayButton';
import NextIcon from '../icons/NextIcon';
import SkipIcon from '../icons/SkipIcon';
import TimesIcon from '../icons/TimesIcon';
import PodcastPlaylist from '../lists/PodcastPlaylist';
import AudioSeekBar from '../partials/AudioSeekBar';
import Blocked from '../partials/Blocked';
import Duration from '../partials/Duration';
import Picture from '../partials/Picture';
import Surtitle from '../partials/Surtitle';
import SummaryTitleTypography from '../typography/SummaryTitle';
import VideoDocument, { VideoDocumentProps } from './VideoDocument';

import styles from '../../styles/documents/episode-document.module.css';

export interface EpisodeDocumentProps extends VideoDocumentProps {
    isPreview?: boolean;
}

type EpisodeMode = 'audio' | 'video';

type SeasonSnippet = CollectionSnippet & { date?: string | null };

function sortByDate(collections: SeasonSnippet[]) {
    return [...collections].sort(({ date: aDate = null }, { date: bDate = null }) =>
        aDate > bDate ? 1 : -1,
    );
}

// The page of a podcast episode: its audio or its video, beside the playlist of its season
function EpisodeDocument({
    id = null,
    type = null,
    slug = null,
    surtitle = null,
    title = null,
    image = null,
    audio = null,
    video = null,
    trailer = null,
    metadata = null,
    access = null,
    current = false,
    autoPlay = false,
    isPreview = false,
    onEnter = null,
    className = null,
    ...videoProps
}: EpisodeDocumentProps) {
    const intl = useIntl();
    const player = useAudioPlayer();
    const url = useDocumentUrlGenerator();
    const [, setLocation] = useLocation();
    const { collections = [], duration: metadataDuration = null } = metadata || {};
    const [collection = null] = collections || [];

    const finalAccess = useAccess({ collection, access });
    const { blocked = false } = finalAccess || {};
    const unblocked = useHasAccess(finalAccess);

    const finalSurtitle = useDocumentSurtitle({ type, surtitle, metadata });

    // Serie and seasons, for the playlist; without a serie, the first collection (as DocumentPage)
    const serieSnippet =
        (collections || []).reduce((acc, coll) => {
            const { serie = null, type: collectionType = null } = coll || {};
            if ((collectionType || '').indexOf('serie') !== -1) {
                return coll || acc;
            }
            return serie || acc;
        }, null) || collection;
    const { collection: loadedSerie } = useCollection(serieSnippet?.slug || null, {
        enabled: serieSnippet !== null,
    });
    const serie = loadedSerie as (Collection & { seasons?: SeasonSnippet[] | null }) | null;
    const seasons = sortByDate(serie?.seasons || []);
    const documentSeasonId =
        (collections || []).find(({ id: collectionId }) =>
            seasons.some(({ id: seasonId }) => seasonId === collectionId),
        )?.id ||
        seasons[seasons.length - 1]?.id ||
        serieSnippet?.id ||
        null;
    const [selectedSeasonId, setSelectedSeasonId] = useState<string | null>(null);
    const playlistSeasonId = selectedSeasonId || documentSeasonId;
    const itemsQuery = (collectionId: string | null) => ({
        collection: collectionId,
        with_unindexed: true,
        order: 'old',
        count: 100,
    });
    const { items: playlistItems = null } = useDocuments(itemsQuery(playlistSeasonId), {
        enabled: playlistSeasonId !== null,
    });
    const { items: seasonItems = null } = useDocuments(itemsQuery(documentSeasonId), {
        enabled: documentSeasonId !== null,
    });

    // Playback, shared with the mini player through the audio player context
    const isLoaded = player !== null && id !== null && player.document?.id === id;
    const playing = isLoaded && player.playing;

    // An episode with a video and an audio opens on its video, unless its audio is playing; the
    // listener can switch between both
    const hasAudio = getAudioUrl(audio) !== null;
    const hasVideo = (video || trailer || null) !== null;
    const [chosenMode, setChosenMode] = useState<{ id: string; mode: EpisodeMode } | null>(null);
    const defaultMode: EpisodeMode = hasVideo && !isLoaded ? 'video' : 'audio';
    const mode: EpisodeMode = !hasAudio
        ? 'video'
        : !hasVideo
          ? 'audio'
          : chosenMode !== null && chosenMode.id === id
            ? chosenMode.mode
            : defaultMode;
    // Without audio, the video page shows the video, or the poster of an episode without one
    const showVideo = mode === 'video';
    const onClickMode = () => {
        const newMode: EpisodeMode = mode === 'video' ? 'audio' : 'video';
        if (newMode === 'video' && playing) {
            player.pause();
        }
        setChosenMode({ id, mode: newMode });
    };
    // The video never plays over the audio
    const onVideoEnter = () => {
        if (player !== null && player.playing) {
            player.pause();
        }
        if (onEnter !== null) {
            onEnter();
        }
    };
    const { progress = null } = useDocumentProgress(id);
    const duration = (isLoaded && player.duration) || audio?.duration || metadataDuration || 0;
    const savedTime = progress !== null && progress < 0.98 ? progress * duration : 0;
    const [startTime, setStartTime] = useState<number | null>(null);
    const currentTime = isLoaded ? player.currentTime : startTime !== null ? startTime : savedTime;

    const ownIndex = (seasonItems || []).findIndex(({ id: itemId }) => itemId === id);
    const nextItem = isLoaded
        ? player.nextItem
        : ownIndex !== -1
          ? seasonItems[ownIndex + 1] || null
          : null;

    const load = (autoPlayNow = true) => {
        if (player === null) {
            return;
        }
        player.load(
            {
                id,
                type: 'podcast',
                slug,
                title,
                image,
                audio,
                metadata,
                access,
            } as PodcastDocument,
            {
                queue: seasonItems || [],
                startTime,
                autoPlay: autoPlayNow,
                withoutTracking: isPreview,
            },
        );
        if (onEnter !== null) {
            onEnter();
        }
    };

    const onClickPlay = () => {
        if (isLoaded) {
            player.toggle();
        } else {
            load();
        }
    };

    const onSeek = (time: number) => {
        if (isLoaded) {
            player.seek(time);
        } else {
            setStartTime(Math.max(0, Math.min(duration, time)));
        }
    };

    // Another episode replaces this one on the same page of the timeline, without adding a page
    const showEpisode = (item: DocumentSnippet) => {
        const itemUrl = url(item);
        if (itemUrl !== null) {
            setLocation(itemUrl, { replace: true, state: samePageHistoryState });
        }
    };

    const onClickNext = () => {
        if (player === null || nextItem === null) {
            return;
        }
        player.playItem(nextItem);
        showEpisode(nextItem);
    };

    const onPlayItem = (item: DocumentSnippet) => {
        if (item.id === id) {
            onClickPlay();
        } else if (player !== null) {
            // A listening session goes on in audio; otherwise an episode opens on its video
            player.playItem(item, { keepVideo: !player.playing });
            showEpisode(item);
        }
    };

    // When this episode ends and the next one starts on its own, the page follows it
    const playingId = player?.document?.id || null;
    const previousPlayingIdRef = useRef<string | null>(playingId);
    useEffect(() => {
        const { current: previousPlayingId } = previousPlayingIdRef;
        previousPlayingIdRef.current = playingId;
        if (!current || previousPlayingId !== id || playingId === null || playingId === id) {
            return;
        }
        const playingItem = (seasonItems || []).find(({ id: itemId }) => itemId === playingId);
        if (playingItem) {
            showEpisode(playingItem);
        }
    }, [playingId]);

    // A new episode on this page starts from its own position
    useEffect(() => {
        setStartTime(null);
    }, [id]);

    const rate = player?.rate || 1;
    const onClickRate = () => {
        const index = audioPlaybackRates.indexOf(rate);
        player?.setRate(audioPlaybackRates[(index + 1) % audioPlaybackRates.length]);
    };

    // The queue follows the playlist of the season once it is loaded
    useEffect(() => {
        if (isLoaded && seasonItems !== null && ownIndex !== -1) {
            player.setQueue(seasonItems);
        }
    }, [isLoaded, seasonItems]);

    // ?autoplay=true in the url (a video plays on its own)
    useEffect(() => {
        if (current && autoPlay && !isLoaded && !showVideo) {
            load();
        }
    }, [current, autoPlay]);

    // The mini player shows up when this player is out of view
    const playerKey = useId();
    const { ref: playerRef, visible } = useIsVisible({ threshold: 0.2 });
    useEffect(() => {
        player?.setPagePlayerVisible(playerKey, current && visible ? id : null);
    }, [current, visible, id]);
    useEffect(() => () => player?.setPagePlayerVisible(playerKey, null), []);

    const modeButton =
        hasAudio && hasVideo ? (
            <BasicButton className={styles.modeButton} onClick={onClickMode}>
                <span className={styles.modeLabel}>
                    {mode === 'video' ? (
                        <FormattedMessage
                            defaultMessage="Audio seulement"
                            description="Podcast episode button"
                        />
                    ) : (
                        <FormattedMessage
                            defaultMessage="Voir la vidéo"
                            description="Podcast episode button"
                        />
                    )}
                </span>
            </BasicButton>
        ) : null;

    return (
        <div className={classNames([styles.container, className])}>
            <div
                className={classNames([styles.player, { [styles.withVideo]: showVideo }])}
                ref={playerRef}
            >
                {showVideo ? (
                    <div className={styles.videoContainer}>
                        <div className={styles.videoFrame}>
                            <VideoDocument
                                {...videoProps}
                                id={id}
                                type={type}
                                surtitle={surtitle}
                                title={title}
                                image={image}
                                video={video}
                                trailer={trailer}
                                metadata={metadata}
                                access={access}
                                current={current}
                                autoPlay={autoPlay}
                                onEnter={onVideoEnter}
                                className={styles.video}
                            />
                        </div>
                        {modeButton}
                    </div>
                ) : (
                    <>
                        <div className={styles.top}>
                            {image !== null ? (
                                <div className={styles.cover}>
                                    <Picture
                                        media={image}
                                        size="large"
                                        fetchPriority="high"
                                        className={styles.image}
                                    />
                                </div>
                            ) : null}
                            <div className={styles.info}>
                                {finalSurtitle !== null ? (
                                    <Surtitle
                                        surtitle={finalSurtitle}
                                        className={styles.surtitle}
                                    />
                                ) : null}
                                <SummaryTitleTypography tag="h2" className={styles.title}>
                                    {title}
                                </SummaryTitleTypography>
                                {duration > 0 ? (
                                    <Duration duration={duration} className={styles.duration} />
                                ) : null}
                                {blocked ? (
                                    <Blocked unblocked={unblocked} className={styles.blocked} />
                                ) : null}
                                {modeButton}
                            </div>
                        </div>
                        <div className={styles.controls}>
                            <AudioSeekBar
                                currentTime={currentTime}
                                duration={duration}
                                onSeek={onSeek}
                                className={styles.seekBar}
                            />
                            <div className={styles.buttons}>
                                <BasicButton
                                    className={styles.button}
                                    onClick={() => onSeek(currentTime - 15)}
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Reculer de 15 secondes',
                                        description: 'Audio player button',
                                    })}
                                >
                                    <SkipIcon seconds={15} className={styles.icon} />
                                </BasicButton>
                                <PlayButton
                                    className={styles.playButton}
                                    iconClassName={classNames([
                                        styles.playIcon,
                                        { [styles.isPlayIcon]: !playing },
                                    ])}
                                    playing={playing}
                                    loading={isLoaded && player.loading}
                                    onClick={onClickPlay}
                                    aria-label={
                                        playing
                                            ? intl.formatMessage({
                                                  defaultMessage: 'Pause',
                                                  description: 'Audio player button',
                                              })
                                            : intl.formatMessage({
                                                  defaultMessage: 'Écouter',
                                                  description: 'Audio player button',
                                              })
                                    }
                                />
                                <BasicButton
                                    className={styles.button}
                                    onClick={() => onSeek(currentTime + 30)}
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Avancer de 30 secondes',
                                        description: 'Audio player button',
                                    })}
                                >
                                    <SkipIcon
                                        seconds={30}
                                        direction="forward"
                                        className={styles.icon}
                                    />
                                </BasicButton>
                                {nextItem !== null ? (
                                    <BasicButton
                                        className={styles.button}
                                        onClick={onClickNext}
                                        aria-label={intl.formatMessage({
                                            defaultMessage: 'Épisode suivant',
                                            description: 'Audio player button',
                                        })}
                                    >
                                        <NextIcon className={styles.icon} />
                                    </BasicButton>
                                ) : null}
                                <span className={styles.spacer} />
                                <BasicButton
                                    className={styles.rateButton}
                                    onClick={onClickRate}
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Vitesse de lecture',
                                        description: 'Audio player button',
                                    })}
                                >
                                    <span className={styles.rateLabel}>
                                        {intl.formatNumber(rate)}
                                    </span>
                                    <TimesIcon className={styles.rateIcon} />
                                </BasicButton>
                                <MuteButton
                                    className={styles.muteButton}
                                    muted={player?.muted || false}
                                    onClick={() => player?.setMuted(!player.muted)}
                                    aria-label={intl.formatMessage({
                                        defaultMessage: 'Couper le son',
                                        description: 'Audio player button',
                                    })}
                                    aria-pressed={player?.muted ? 'true' : 'false'}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
            {serieSnippet !== null ? (
                <PodcastPlaylist
                    title={serie?.title || serieSnippet.title || null}
                    seasons={seasons}
                    seasonId={playlistSeasonId}
                    items={playlistItems}
                    currentId={id}
                    playing={playing}
                    autoplayNext={player?.autoplayNext ?? true}
                    onChangeSeason={setSelectedSeasonId}
                    onToggleAutoplay={() => player?.setAutoplayNext(!player.autoplayNext)}
                    onPlayItem={onPlayItem}
                    className={styles.playlist}
                />
            ) : null}
        </div>
    );
}

export default EpisodeDocument;
